"use client";

import React, { useEffect } from "react";
import Link from "next/link";
import MenuItem from "../../MenuItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/sharp-light-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import { HeaderProps } from "../models";
import {
  getGap,
  getPaddings,
  getTextSize,
  getVisibleValue,
} from "@/utils/theme-helper";
import { twMerge } from "tailwind-merge";
import { faCircleUser } from "@fortawesome/pro-light-svg-icons";
import CtaComponent from "@/components/ui/CtaComponent";
import SearchBar from "@/components/ui/SearchBar";
const TransparentHeader: React.FC<HeaderProps> = ({
  cmsData,
  appData,
  header,
}) => {
  const logoUrl = appData?.logo?.originalUrl;
  const inverseLogoUrl = appData?.inverseLogo?.originalUrl;
  const [isVisible, setIsVisible] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  const [showSearch, setShowSearch] = React.useState(false);

  const menuItems = cmsData?.pages
    .filter((x) => x.includeInMenu)
    .map((page) => ({
      title: page.menuTitle,
      href: page.slug,
      redirectToExternal: page.redirectToExternal,
      openInNewTab: page.openInNewTab,
      externalUrl: page.externalUrl,
    }));

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const toggleSearch = () => {
    if (!showSearch) {
      setIsVisible(true);
    } else {
      setIsVisible(
        header.isVisible || window.scrollY > (header?.scrollThreshold ?? 1)
      );
    }
    setShowSearch(!showSearch);
  };

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape" && showSearch) {
      toggleSearch();
    }
    if (event.key === "Escape" && showMenu) {
      setShowMenu(false);
    }
  }

  function handleScroll() {
    if (!window) {
      return;
    }

    if (window.scrollY > (header?.scrollThreshold ?? 1)) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearch, showMenu]);

  useEffect(() => {
    if (!window || header.isVisible) {
      return;
    }

    if (window.scrollY > (header?.scrollThreshold ?? 1)) {
      setIsVisible(true);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  let customerPortalUrl =
    cmsData?.b2cLink ?? appData?.resourceGroup?.primaryB2CApplication?.url;
  if (customerPortalUrl && !customerPortalUrl.startsWith("https://")) {
    customerPortalUrl = "https://" + customerPortalUrl.replace("http://", "");
  }

  return (
    <div id="TransparentHeader" className="relative">
      {header.overlay && !showSearch && (
        <div
          style={{
            backgroundImage: `linear-gradient(to bottom, ${header.overlay.fromBgColor}, rgba(255,0,0,0))`,
          }}
          className="z-30 absolute inset-0 h-[80px]"
        ></div>
      )}

      <header
        data-isvisible={isVisible || header.isVisible}
        className={`h-[80px] 
           data-[isvisible=true]:h-[60px] 
           data-[isvisible=true]:shadow-md 
           transition-[height] ease-in-out duration-200 
           data-[isvisible=true]:bg-[var(--header-bg-color)] 
           data-[isvisible=false]:text-[var(--header-text-color)] 
           data-[isvisible=true]:text-[var(--header-visible-text-color)] 
           lg:h-[80px] flex 
           justify-between 
           data-[isvisible=false]:bg-transparent fixed top-0 left-0 right-0 z-30`}
      >
        <div
          className={`fixed inset-0 bg-gray-800 z-20 transition-opacity duration-300 ${
            showMenu ? "opacity-40" : "opacity-0 pointer-events-none"
          }`}
        ></div>
        <AnimatePresence>
          {showMenu && (
            <motion.div
              initial="hidden"
              animate="visible"
              className={`flex shadow-md text-[var(--menu-text-color)] h-dvh fixed top-0 z-20 right-0 left-0 lg:left-auto bottom-0 lg:w-[380px] pt-28 pl-[30px]`}
            >
              <motion.div
                className="bg-[var(--menu-bg-color)] absolute inset-0 z-20"
                variants={{
                  visible: {
                    clipPath: `circle(300% at calc(100% - 54px) 50px)`,
                    transition: {
                      type: "spring",
                      stiffness: 20,
                      restDelta: 2,
                    },
                  },
                  hidden: {
                    clipPath: "circle(0px at calc(100% - 54px) 50px)",
                    transition: {
                      delay: 0.2,
                      type: "spring",
                      stiffness: 400,
                      damping: 40,
                    },
                  },
                }}
              ></motion.div>
              <motion.div
                variants={{
                  visible: {
                    transition: { staggerChildren: 0.02, delayChildren: 0.3 },
                  },
                  hidden: {
                    transition: { staggerChildren: 0.02, staggerDirection: -1 },
                  },
                }}
                className={`flex flex-col gap-4 relative z-20 flex-1 overflow-y-auto pb-10 ${getTextSize(header.menuTextSize, "lg")}`}
              >
                {menuItems?.map((item, index) => (
                  <motion.div
                    key={`${index}-menu`}
                    variants={{
                      visible: {
                        y: 0,
                        opacity: 1,
                      },
                      hidden: {
                        y: 50,
                        opacity: 0,
                        transition: {
                          duration: 0.05,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      key={item.href}
                      href={item.href}
                      onClick={() => setShowMenu(false)}
                      redirectToExternal={item.redirectToExternal}
                      openInNewTab={item.openInNewTab}
                      externalUrl={item.externalUrl}
                    >
                      {item.title}
                    </MenuItem>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          className={`flex px-[20px] lg:px-[40px] ${getGap(cmsData?.layout?.header?.gap)} flex-1 justify-between z-30`}
        >
          <Link
            aria-label="Home"
            className={`flex ${cmsData?.layout?.header?.padding ? getPaddings(cmsData?.layout?.header?.padding) : ""}`}
            href={"/"}
          >
            {(cmsData?.logoUrl || logoUrl) && (
              <img
                data-isvisible={(isVisible || header.isVisible) ?? false}
                src={
                  isVisible || header.isVisible
                    ? inverseLogoUrl ||
                      cmsData?.inverseLogoUrl ||
                      logoUrl ||
                      cmsData?.logoUrl
                    : logoUrl || cmsData?.logoUrl
                }
                alt="logo"
                data-showmenu={showMenu}
                height={0}
                width={150}
                className={`py-4 h-[80px] object-contain data-[showmenu=true]:hidden data-[showmenu=true]:md:flex data-[isvisible=true]:h-[60px] transition-all duration-200 lg:h-[80px] w-auto ${cmsData?.layout?.header?.paddingTransparent ? getPaddings(cmsData?.layout?.header?.paddingTransparent) : ""}`}
              />
            )}
          </Link>
          <div className="flex gap-2 md:gap-8 items-center justify-end">
            {cmsData?.layout?.header?.ctas?.map((x, index) => (
              <CtaComponent
                key={`header-cta-${index}`}
                id={`header-cta-${index}`}
                config={x}
                itemIndex={index}
              />
            ))}
            <button
              data-hideicon={showMenu || showSearch}
              data-isvisible={(isVisible || header.isVisible) ?? false}
              data-menu-open={showMenu}
              data-foo="Asd"
              aria-label="Search"
              className={twMerge(
                "p-[8px] data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)]",
                "data-[menu-open=true]:text-[var(--menu-text-color)]",
                getVisibleValue(cmsData?.layout?.header?.showSearch)
              )}
              onClick={() => toggleSearch()}
            >
              <FontAwesomeIcon
                className="size-[24px] flex"
                icon={faMagnifyingGlass}
              />
            </button>
            {customerPortalUrl && (
              <Link
                data-hideicon={showMenu || showSearch}
                data-menu-open={showMenu}
                aria-label="Kundeunivers"
                data-isvisible={(isVisible || header.isVisible) ?? false}
                target="_blank"
                href={customerPortalUrl}
                className={twMerge(
                  "p-[8px] data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)]",
                  "data-[menu-open=true]:text-[var(--menu-text-color)]",
                  getVisibleValue(cmsData?.layout?.header?.showB2c)
                )}
              >
                <FontAwesomeIcon
                  className="size-[24px] flex"
                  icon={faCircleUser}
                />
              </Link>
            )}
            <button
              aria-label="Menu"
              onClick={() => (showSearch ? toggleSearch() : toggleMenu())}
              className=" size-10 relative focus:outline-none rounded group"
            >
              <div className="block w-7 absolute left-6 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span
                  data-isvisible={isVisible || header.isVisible || showMenu}
                  data-menu-open={showMenu}
                  className={twMerge(
                    "block absolute right-0 h-0.5 data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)] group-hover:w-full bg-current transition-all transform duration-300 ease-in-out",
                    "data-[menu-open=true]:text-[var(--menu-text-color)]",
                    showMenu || showSearch
                      ? "rotate-45 w-full "
                      : "-translate-y-2 w-[70%]"
                  )}
                ></span>
                <span
                  data-isvisible={isVisible || header.isVisible || showMenu}
                  data-menu-open={showMenu}
                  className={twMerge(
                    "block absolute right-0 h-0.5  group-hover:w-full bg-current  transition-all  transform duration-300 ease-in-out data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)]",
                    "data-[menu-open=true]:text-[var(--menu-text-color)]",
                    showMenu || showSearch ? "opacity-0" : "w-[60%]"
                  )}
                ></span>
                <span
                  data-isvisible={isVisible || header.isVisible || showMenu}
                  data-menu-open={showMenu}
                  className={twMerge(
                    "block absolute right-0 h-0.5 w-full bg-current transform  transition duration-300 ease-in-out data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)]",
                    "data-[menu-open=true]:text-[var(--menu-text-color)]",
                    showMenu || showSearch ? "-rotate-45" : "translate-y-2"
                  )}
                ></span>
              </div>
            </button>
          </div>
        </div>
      </header>
      <SearchBar
        cmsData={cmsData!}
        toggleSearch={() => toggleSearch()}
        showSearch={showSearch}
      />
    </div>
  );
};

export default TransparentHeader;
